import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { getModuleZIndex } from '@/lib/furnitureData';
import TemplatePreview from './TemplatePreview';

const SCALE = 0.01;
const THUMB_W = 640;
const THUMB_H = 480;

let sharedRenderer = null;
let sharedEnvMap = null;
const thumbCache = new Map();
const renderQueue = [];
let queueRunning = false;

function runQueue() {
  if (queueRunning) return;
  queueRunning = true;
  const step = () => {
    const job = renderQueue.shift();
    if (!job) {
      queueRunning = false;
      return;
    }
    try {
      const url = renderThumbnail(job.template);
      job.resolve(url);
    } catch (err) {
      job.resolve(null);
    }
    if (renderQueue.length) {
      requestAnimationFrame(step);
    } else {
      queueRunning = false;
    }
  };
  requestAnimationFrame(step);
}

function enqueueThumbnail(template) {
  if (thumbCache.has(template.id)) {
    return Promise.resolve(thumbCache.get(template.id));
  }
  return new Promise((resolve) => {
    renderQueue.push({ template, resolve });
    runQueue();
  });
}

function getRenderer() {
  if (sharedRenderer) return sharedRenderer;
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_W;
  canvas.height = THUMB_H;
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(THUMB_W, THUMB_H, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const pmrem = new THREE.PMREMGenerator(renderer);
  sharedEnvMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  pmrem.dispose();

  sharedRenderer = renderer;
  return renderer;
}

function getZPosition(mod, maxDepth) {
  const d = mod.depth * SCALE;
  const md = maxDepth * SCALE;
  if (typeof mod.z_pos === 'number') {
    return (mod.z_pos + mod.depth / 2) * SCALE;
  }
  switch (mod.type) {
    case 'back_panel': return d / 2;
    case 'door': return md - d / 2;
    case 'drawer': return Math.max(d / 2 + 0.005, md - d / 2 - 0.005);
    default: return md / 2;
  }
}

function materialFor(mod) {
  const baseColor = mod.color_hex || '#C4A76C';
  if (mod.material === 'glass') {
    return new THREE.MeshPhysicalMaterial({
      color: baseColor,
      transparent: true,
      opacity: 0.35,
      roughness: 0.06,
      metalness: 0,
      transmission: 0.85,
      ior: 1.45,
      thickness: 0.02,
      envMap: sharedEnvMap,
    });
  }
  if (mod.material === 'metal') {
    return new THREE.MeshStandardMaterial({
      color: baseColor,
      metalness: 0.8,
      roughness: 0.3,
      envMap: sharedEnvMap,
    });
  }
  const isWood = mod.material === 'solid_wood' || mod.material === 'chipboard' ||
    mod.material === 'mdf' || mod.material === 'shelving_ldsp_16' ||
    mod.material === 'shelving_ldsp_22';
  return new THREE.MeshStandardMaterial({
    color: baseColor,
    roughness: isWood ? 0.65 : 0.55,
    metalness: 0.05,
    envMap: sharedEnvMap,
  });
}

function buildScene(template) {
  const scene = new THREE.Scene();
  scene.background = null;
  scene.environment = sharedEnvMap;

  const modules = template?.modules || [];
  if (!modules.length) return { scene, bounds: null };

  const maxDepth = Math.max(...modules.map(m => m.depth), 30);

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  modules.forEach(m => {
    minX = Math.min(minX, m.x * SCALE);
    maxX = Math.max(maxX, (m.x + m.width) * SCALE);
    minY = Math.min(minY, -(m.y + m.height) * SCALE);
    maxY = Math.max(maxY, -m.y * SCALE);
  });
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const cz = maxDepth * SCALE / 2;

  const group = new THREE.Group();
  const sorted = [...modules].sort(
    (a, b) => getModuleZIndex(a.type) - getModuleZIndex(b.type)
  );

  sorted.forEach((mod) => {
    const w = Math.max(0.005, mod.width * SCALE);
    const h = Math.max(0.005, mod.height * SCALE);
    const d = Math.max(0.005, mod.depth * SCALE);
    const x = (mod.x + mod.width / 2) * SCALE - cx;
    const y = -(mod.y + mod.height / 2) * SCALE - cy;
    const z = getZPosition(mod, maxDepth) - cz;

    const geometry = new THREE.BoxGeometry(w, h, d);
    const material = materialFor(mod);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  });

  scene.add(group);

  const floorY = minY - cy - 0.002;
  const floorSize = Math.max(maxX - minX, maxDepth * SCALE) * 2.2;
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(floorSize, floorSize),
    new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.22 })
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = floorY;
  floor.receiveShadow = true;
  scene.add(floor);

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
  keyLight.position.set(2.5, 4, 3);
  keyLight.castShadow = true;
  keyLight.shadow.mapSize.set(1024, 1024);
  const s = 3;
  keyLight.shadow.camera.left = -s;
  keyLight.shadow.camera.right = s;
  keyLight.shadow.camera.top = s;
  keyLight.shadow.camera.bottom = -s;
  keyLight.shadow.camera.near = 0.1;
  keyLight.shadow.camera.far = 20;
  keyLight.shadow.bias = -0.0005;
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
  fillLight.position.set(-3, 2, 2);
  scene.add(fillLight);

  scene.add(new THREE.AmbientLight(0xffffff, 0.35));

  const bounds = {
    minX: minX - cx, maxX: maxX - cx,
    minY: minY - cy, maxY: maxY - cy,
    depth: maxDepth * SCALE,
  };
  return { scene, bounds };
}

function disposeScene(scene) {
  scene.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => m.dispose());
    }
  });
}

function renderThumbnail(template) {
  if (thumbCache.has(template.id)) return thumbCache.get(template.id);

  const renderer = getRenderer();
  const { scene, bounds } = buildScene(template);
  if (!bounds) return null;

  const camera = new THREE.PerspectiveCamera(32, THUMB_W / THUMB_H, 0.05, 50);

  const spanX = Math.max(0.3, bounds.maxX - bounds.minX);
  const spanY = Math.max(0.3, bounds.maxY - bounds.minY);
  const spanZ = bounds.depth;
  const radius = Math.max(spanX, spanY, spanZ);

  const distance = radius * 2.3 + 0.4;
  camera.position.set(distance * 0.75, distance * 0.55, distance * 0.95);
  camera.lookAt(0, (bounds.minY + bounds.maxY) / 2 * 0.3, 0);

  renderer.setClearColor(0x000000, 0);
  renderer.render(scene, camera);

  const dataUrl = renderer.domElement.toDataURL('image/png');
  disposeScene(scene);

  thumbCache.set(template.id, dataUrl);
  return dataUrl;
}

export default function TemplateThumbnail3D({ template, className = '' }) {
  const [src, setSrc] = useState(() => (template?.id ? thumbCache.get(template.id) : null) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!template?.modules?.length) return undefined;
    if (thumbCache.has(template.id)) {
      setSrc(thumbCache.get(template.id));
      return undefined;
    }

    let cancelled = false;
    enqueueThumbnail(template).then((url) => {
      if (cancelled) return;
      if (url) setSrc(url);
      else setFailed(true);
    });

    return () => { cancelled = true; };
  }, [template]);

  if (failed) {
    return <TemplatePreview template={template} className={className} />;
  }

  if (!src) {
    return (
      <div className={`w-full h-full flex items-center justify-center ${className}`}>
        <div className="flex flex-col items-center gap-2 text-[#9A9A9A]">
          <div className="w-6 h-6 border-2 border-[#C25934]/30 border-t-[#C25934] rounded-full animate-spin" />
          <span className="text-[10px] uppercase tracking-wider">3D рендер</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={template?.name_ru || '3D превью мебели'}
      className={`w-full h-full object-contain ${className}`}
      loading="lazy"
    />
  );
}
